import { useState } from 'react';
import * as React from 'react';
import { observer } from 'mobx-react';

import { ROUTES } from 'Constants';
import { appConfigStore, RefundMethod } from 'Models/AppConfigStore';
import { BookingCutoffEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { cancelBooking } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import alertToast from 'Util/ToastifyUtils';
import { stringIsEmpty } from 'Util/TypeGuards';
import { CancellationWizard } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/CancellationWizard';
import {
	CancellationResult,
	useCancellationResult,
} from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/CancellationResult';
import { upperCaseFirst } from 'Util/StringUtils';

// Types
export interface BookingCancellationDto {
	bookingId: string;
	cancelReturnBooking: boolean;
	amountToRefund?: number;
	recommendedRefundAmount?: number;
	refundMethod: RefundMethod;
	// Manager-only fee override for invoiced bookings (which have no refund to adjust).
	// Undefined uses the calculated fee; a number (including 0, to waive) replaces it.
	cancellationFeeOverride?: number;
	// The reason chosen on the confirmation step, and the free text for a reason that asks for more
	// information. Both re-validated server-side.
	cancellationReasonId?: string;
	cancellationReasonDetail?: string;
	/** Cancelled from check-in, so the trip history shows it as done at check-in. */
	inCheckIn?: boolean;
}

export interface RefundCardDto {
	cardBrand?: string;
	last4?: string;
	totalRefundableAmount?: number;
}

export interface BookingCancellationInformationDto {
	bookings: BookingCancellationRefundDto[];
	invoiced: boolean;
	isUnpaid: boolean;
	discounted?: boolean;
	maximumRefund: number;
	recommendedRefundAmount: number;
	bankRecommendedRefundAmount: number;
	creditRecommendedRefundAmount: number;
	// Cancellation fee charged. For invoiced bookings this is the real fee (no refund to derive it
	// from) and drives the modal display; for paid bookings it matches the implied fee.
	cancellationFee: number;
	cardsToRefund?: RefundCardDto[];
}

/**
 * One fare type's part of a cancellation fee.
 *
 * <p>A booking whose passengers travel in different fare types is priced one fare type at a time,
 * each by its own rule, and the results added together — so `bookingCutoff` alone is the
 * dearest-contributing rule rather than the whole story.</p>
 */
export interface CancellationFeeGroup {
	passengerClassName: string | null;
	passengerCount: number;
	chargedAgainst: number;
	/** Null when no rule matched this fare type — it is charged nothing. */
	feeAmount: number | null;
	staticFee: boolean;
	contribution: number;
}

export interface BookingCancellationRefundDto {
	id: string;
	startStopId: string;
	endStopId: string;
	departureDateTime?: string | Date | null;
	mainBooking: boolean;
	maximumRefund: number;
	recommendedRefundAmount: number;
	bankRecommendedRefundAmount: number;
	creditRecommendedRefundAmount: number;
	cancellationFee: number;
	bookingCutoff?: BookingCutoffEntity;
	bankBookingCutoff?: BookingCutoffEntity;
	creditBookingCutoff?: BookingCutoffEntity;
	/** Empty on a booking with no fare types, which one rule describes completely. */
	feeGroups?: CancellationFeeGroup[];
	bankFeeGroups?: CancellationFeeGroup[];
	creditFeeGroups?: CancellationFeeGroup[];
	// Overrides the route-based "Start to End" label when the item isn't a ferry leg
	// (events, parking reservations etc.). Optional subtitle replaces departureDateTime.
	displayLabel?: string;
	displaySubtitle?: string;
}

interface CancelBookingModalArgs {
	bookingId: string;
	userFirstName: string;
	userLastName: string;
	cancellationInformation: BookingCancellationInformationDto;
	refresh: () => void;
	bookingLegacyId?: any;
}

export interface ModalConfig {
	title: string;
	content: JSX.Element;
	cancelButtonText: string;
	confirmButtonText: string;
	disableConfirmButtonFunc?: () => void;
	disableCancelButton?: boolean;
}

export interface RefundModel {
	selectedBookings: string[];
	refundAmount: number;
	refundAmountErrors: string;
	bookingRefunds: { [bookingId: string]: number };
	forceCreditRefund: boolean;
	// When true the refund is recorded but not processed automatically — staff settle it
	// offline. Treats forceCreditRefund as false (uses the bank-equivalent refund quote).
	forceManualRefund: boolean;
	// Manager fee override for invoiced bookings (no refund to adjust). Undefined = use the
	// calculated fee; a number (including 0, to waive) replaces it. Ignored for paid bookings.
	cancellationFeeOverride?: number;
	// Chosen on the confirmation step. Undefined when the tenant does not require a reason, or when
	// none applied to this booking and the picker was not rendered.
	cancellationReasonId?: string;
	cancellationReasonDetail?: string;
}

// Maps the two wizard booleans to the wire-level enum the backend expects. Manual takes
// precedence over Credit when both are accidentally set.
export const resolveRefundMethod = (model: {
	forceCreditRefund: boolean;
	forceManualRefund: boolean;
}): RefundMethod => {
	if (model.forceManualRefund) return 'Manual';
	if (model.forceCreditRefund) return 'Credit';
	return 'Bank';
};

// Helper functions
export const getRefundTerminology = (userIsStaff: boolean = store.isStaff) => {
	return appConfigStore.allowedRefundMethodsFor(userIsStaff).has('Credit') ? 'credit' : 'refund';
};

export const getTravelCreditTerminology = (
	forceFerryCreditRefund: boolean = false,
	userIsStaff: boolean = store.isStaff,
) => {
	const creditAllowed = appConfigStore.allowedRefundMethodsFor(userIsStaff).has('Credit');
	return creditAllowed || forceFerryCreditRefund ? 'travel credit' : 'refund';
};

// Whether the cancellation modal should offer a bank-vs-credit choice. Both refund methods
// must be allowed for the user type for the choice to be meaningful.
export const refundMethodChoiceAvailable = (userIsStaff: boolean = store.isStaff): boolean => {
	const allowed = appConfigStore.allowedRefundMethodsFor(userIsStaff);
	return allowed.has('Bank') && allowed.has('Credit');
};

// The pre-selected refund method for cancellation flows. Honours `Refunds.defaultMethod`
// when that method is allowed for the actor; otherwise falls back to Credit → Bank → Manual.
// Manual is only ever pre-selected for staff actors — customers see Bank/Credit only.
export const defaultRefundMethod = (userIsStaff: boolean = store.isStaff): RefundMethod => {
	const allowed = appConfigStore.allowedRefundMethodsFor(userIsStaff);
	const preferred = appConfigStore.refundsDefaultMethod;
	const isManualOk = (m: RefundMethod) => m !== 'Manual' || userIsStaff;
	if (allowed.has(preferred) && isManualOk(preferred)) return preferred;
	if (allowed.has('Credit')) return 'Credit';
	if (allowed.has('Bank')) return 'Bank';
	return 'Manual';
};

// Legacy boolean helper retained for callers that only care about credit vs bank.
export const defaultForceCreditRefund = (userIsStaff: boolean = store.isStaff): boolean => {
	return defaultRefundMethod(userIsStaff) === 'Credit';
};

// True when the only allowed method for this user type is Manual — the backend falls into
// the manual-refund branch and a staff member processes the refund offline. The wizard
// should explain this rather than mislabel it as a "Bank refund".
export const isManualRefundOnly = (userIsStaff: boolean = store.isStaff): boolean => {
	const allowed = appConfigStore.allowedRefundMethodsFor(userIsStaff);
	return !allowed.has('Bank') && !allowed.has('Credit');
};

// Per-method recommended refund amount for a single booking. The bank/credit amounts already
// have the matching cutoff fee deducted on the server.
export const getMethodRefundAmount = (booking: BookingCancellationRefundDto, forceCreditRefund: boolean): number => {
	return forceCreditRefund ? booking.creditRecommendedRefundAmount : booking.bankRecommendedRefundAmount;
};

export const getMethodBookingCutoff = (
	booking: BookingCancellationRefundDto,
	forceCreditRefund: boolean,
): BookingCutoffEntity | undefined => {
	return forceCreditRefund ? booking.creditBookingCutoff : booking.bankBookingCutoff;
};

/** The breakdown for whichever method's quote is on screen. Mirrors getMethodBookingCutoff. */
export const getMethodFeeGroups = (
	booking: BookingCancellationRefundDto,
	forceCreditRefund: boolean,
): CancellationFeeGroup[] =>
	(forceCreditRefund ? booking.creditFeeGroups : booking.bankFeeGroups) ?? [];

export const computeSelectedTotalForMethod = (
	bookings: BookingCancellationRefundDto[],
	selectedIds: string[],
	forceCreditRefund: boolean,
): number => {
	return bookings
		.filter(b => selectedIds.includes(b.id))
		.reduce((sum, b) => sum + getMethodRefundAmount(b, forceCreditRefund), 0);
};

// Handler function for refund changes
export const handleRefundChange = (bookingId: string, amount: number, refundModel: RefundModel | RefundModel) => {
	refundModel.bookingRefunds[bookingId] = amount;
	// Recalculate total refund amount
	refundModel.refundAmount = Object.values(refundModel.bookingRefunds).reduce((sum, val) => sum + val, 0);
};

// Permission and eligibility checks
export const canCancelMigratedBookingWithNoLimit = (bookingLegacyId: any) => {
	return !appConfigStore.migratedBookingsModificationEnabled && !!bookingLegacyId;
};

const canIssueRefund = (bookingLegacyId: any) => {
	const canCancelMigrated = canCancelMigratedBookingWithNoLimit(bookingLegacyId);
	return (canCancelMigrated && store.isStaff) || store.isManager;
};

// Response handling. Returns the cancellation's transaction id when `stayInWizard` is set and the
// cancellation succeeded — the caller then shows the result itself instead of the booking-cancelled page.
const handleCancellationResponse = async (
	navigate: NavigateFunction,
	response: any,
	userFirstName: string,
	userLastName: string,
	refresh: () => void,
	bookingId?: string,
	stayInWizard: boolean = false,
): Promise<string | undefined> => {
	switch (response.status) {
		case 200: {
			const transactionId = response.data?.transactionId ?? '';
			if (stringIsEmpty(transactionId)) {
				alertToast('Failed to cancel booking', 'error');
				return undefined;
			}

			// The booking is cancelled either way. followUpFailed only means a step after that did not
			// complete — the cancellation email being the one worth telling the operator about — so it
			// is a warning alongside the success page, not a failure.
			if (response.data?.followUpFailed) {
				alertToast(
					'The booking was cancelled, but the cancellation email could not be sent.',
					'warning',
				);
			}

			if (stayInWizard) {
				return transactionId;
			}

			navigate(
				ROUTES.BOOKING_CANCELLED_SUCCESS.url(transactionId, userFirstName, userLastName, bookingId, false),
			);
			return undefined;
		}

		case 400:
			alertToast('Unable to cancel booking', 'error');
			return undefined;

		case 409:
			await refresh();
			alertToast('Booking has already been cancelled. Refreshing page...', 'error');
			return undefined;

		default:
			alertToast('An unexpected error occurred', 'error');
			return undefined;
	}
};

type CancelBookingModalProps = {
	bookingId: string;
	userFirstName: string;
	userLastName: string;
	refresh: () => void;
	bookingLegacyId: string;
	cancellationInfo: BookingCancellationInformationDto;
	warnings?: string[];
	blockers?: string[];
	onClose: () => void;
	/**
	 * When set, a successful cancellation stays in the wizard and shows its result there — the same
	 * content as the booking-cancelled page — instead of navigating to that page. Called when the user
	 * dismisses the result. Check-in uses this so the operator never leaves check-in.
	 */
	onCancelled?: () => void;
	/** Opened from check-in. Only labels the trip history row. */
	inCheckIn?: boolean;
};

export const CancelBookingModal: React.FC<CancelBookingModalProps> = observer(
	({
		bookingId,
		userFirstName,
		userLastName,
		refresh,
		bookingLegacyId,
		cancellationInfo,
		warnings,
		blockers,
		onClose,
		onCancelled,
		inCheckIn,
	}) => {
		const navigate = useNavigate();
		const [submitting, setSubmitting] = useState(false);
		const [resultTransactionId, setResultTransactionId] = useState<string>();

		const handleConfirm = async (refundModel: RefundModel, recommendedRefundAmount: number) => {
			if (submitting) return false;
			setSubmitting(true);
			try {
				const response = await cancelBooking({
					bookingId,
					cancelReturnBooking: refundModel.selectedBookings.length > 1,
					amountToRefund: refundModel.refundAmount,
					recommendedRefundAmount,
					refundMethod: resolveRefundMethod(refundModel),
					cancellationFeeOverride: refundModel.cancellationFeeOverride,
					cancellationReasonId: refundModel.cancellationReasonId,
					cancellationReasonDetail: refundModel.cancellationReasonDetail,
					inCheckIn,
				});

				const transactionId = await handleCancellationResponse(
					navigate,
					response,
					userFirstName,
					userLastName,
					refresh,
					bookingId,
					!!onCancelled,
				);

				if (transactionId) {
					setResultTransactionId(transactionId);
					return true;
				}

				onClose?.();
				return true;
			} catch (error) {
				console.error('Error canceling booking:', error);
				alertToast('An error occurred while canceling the booking', 'error');
				onClose?.();
				return false;
			} finally {
				setSubmitting(false);
			}
		};

		return (
			<CancellationWizard
				isOpen
				cancellationInformation={cancellationInfo}
				bookingLegacyId={bookingLegacyId}
				reasonBookingId={bookingId}
				warnings={warnings}
				blockers={blockers}
				onClose={() => (resultTransactionId ? onCancelled?.() : onClose?.())}
				onConfirm={handleConfirm}
				result={
					resultTransactionId && (
						<CancellationResultStep
							transactionId={resultTransactionId}
							bookingId={bookingId}
							userFirstName={userFirstName}
							userLastName={userLastName}
						/>
					)
				}
			/>
		);
	},
);

// Mounted only once the transaction exists, so the result's loading states start true and its
// sections show skeletons rather than nothing while they load.
function CancellationResultStep({
	transactionId,
	bookingId,
	userFirstName,
	userLastName,
}: {
	transactionId: string;
	bookingId: string;
	userFirstName: string;
	userLastName: string;
}) {
	const result = useCancellationResult(transactionId, bookingId);
	// Same normalisation as the booking-cancelled page, so the spiel reads "John Smith's …".
	const userName =
		`${upperCaseFirst(userFirstName.toLowerCase())} ${upperCaseFirst(userLastName.toLowerCase())}`.trim();

	return <CancellationResult result={result} userName={userName} isEventBooking={false} linkTrips={false} />;
}
